import { DEFAULT_LANGUAGE, resolveSupportedLanguage } from '@/constants/languages';
import type { AnswerDepth } from '@/components/chat/AnswerDepth';

export type BallotRefKind = 'candidate' | 'contest' | 'measure' | 'officeholder';

/**
 * The ballot row a message was sent from — a tapped "Full profile", "Explain"
 * or "Compare" control. The whole ballot already travels in `context`; this
 * says which row was tapped, so the backend renders that race's own candidate
 * and seat counts (or the measure's ballot question) into the ballot block
 * instead of leaving the model to re-derive them from the web. Resolved by
 * exact id/name match against `context`; an unmatched ref is ignored.
 *
 * Mirrors `ChatRequest.ballot_ref` in the backend's `chat_controller.py`.
 */
export interface BallotRef {
  kind: BallotRefKind;
  contest_id?: string;
  candidate_name?: string;
  measure_id?: string;
  officeholder_name?: string;
}

/** The backend bounds each ref string; a longer one rejects the whole turn. */
const BALLOT_REF_MAX_CHARS = 200;

const BALLOT_REF_FIELDS = [
  'contest_id',
  'candidate_name',
  'measure_id',
  'officeholder_name',
] as const;

/** Trim, drop empty fields, and clamp to the backend's bound — a clamped value
 *  matches nothing server-side, where an over-long one fails the request. */
const ballotRefForRequest = (ref: BallotRef): BallotRef => {
  const out: BallotRef = { kind: ref.kind };
  for (const field of BALLOT_REF_FIELDS) {
    const value = ref[field]?.trim();
    if (value) out[field] = value.slice(0, BALLOT_REF_MAX_CHARS);
  }
  return out;
};

/**
 * Inputs for the `/chat/stream` request body. Split out of `ChatContainer` so
 * the body is testable without a network call or a rendered tree.
 *
 * Mirrors `votemate-mobile-app/src/services/chatRequest.ts`, plus
 * `embedSourceUrl` (web-only).
 */
export interface ChatRequestInputs {
  message: string;
  conversationId: string | null;
  /** Active interface language, in whatever casing the detector produced. */
  language: string | null | undefined;
  context?: unknown;
  clientTimezone?: string | null;
  participantId?: string | null;
  campaignCode?: string | null;
  embedSourceUrl?: string | null;
  depth?: AnswerDepth;
  /** Set only when a ballot control sent the message; a typed message has none. */
  ballotRef?: BallotRef | null;
}

export interface ChatRequestBody {
  conversation_id: string | null;
  message: string;
  language: string;
  context?: unknown;
  client_timezone?: string;
  participant_id?: string;
  campaign_code?: string;
  embed_source_url?: string;
  depth?: AnswerDepth;
  ballot_ref?: BallotRef;
}

/**
 * Build the `/chat/stream` request body.
 *
 * `language` is always sent and always canonical-cased (`zh-Hant`, never
 * `zh-tw`) — the backend coerces codes it doesn't know to `en`, so a bad case
 * answers in English instead of erroring. Other fields are omitted when absent,
 * never sent as null — a partner's cached embed can run against an older
 * backend, so the default request stays exactly what it was.
 */
export const buildChatRequestBody = (inputs: ChatRequestInputs): ChatRequestBody => {
  const {
    message,
    conversationId,
    language,
    context,
    clientTimezone,
    participantId,
    campaignCode,
    embedSourceUrl,
    depth,
    ballotRef,
  } = inputs;

  return {
    conversation_id: conversationId,
    message,
    language: resolveSupportedLanguage(language ?? null) ?? DEFAULT_LANGUAGE,
    ...(context ? { context } : {}),
    ...(clientTimezone ? { client_timezone: clientTimezone } : {}),
    ...(participantId ? { participant_id: participantId } : {}),
    ...(campaignCode ? { campaign_code: campaignCode } : {}),
    ...(embedSourceUrl ? { embed_source_url: embedSourceUrl } : {}),
    ...(depth ? { depth } : {}),
    ...(ballotRef ? { ballot_ref: ballotRefForRequest(ballotRef) } : {}),
  };
};
